import * as React from 'react';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import { throttle } from 'throttle-debounce';

import { getConfig, URLS } from '@constants';

import { ELocalStorageMobile } from '@enums';

import { CurrentUserStore, ICountersStore, IUiStoreParams } from '@stores';

import { EStore } from '@base/enums';
import { useCountersWatchers } from '@base/hooks';
import { isMobileScreen, setToLocalStorage, getFromLocalStorage } from '@base/utils';

import { Alert } from '@components';

import { CatalogTabs } from '@ui';

import { Header } from './Header';

import * as s from './TopMenu.scss';

export interface ITopMenuProps {
	uiStore?: IUiStoreParams;
	countersStore?: ICountersStore;
	currentUserStore?: CurrentUserStore;
}

export const TopMenu = inject(
	EStore.UI,
	EStore.COUNTERS,
	EStore.CURRENT_USER,
)(observer((
	{
		uiStore,
		countersStore,
		currentUserStore,
	}: ITopMenuProps,
) => {
	const { topMenuBurgerButton, slidingMenu } = uiStore;
	const {
		notifies,
		dialogues,
	} = countersStore;

	const history = useHistory();
	const { pathname } = useLocation();

	const [backPath, setBackPath] = React.useState('');
	const [showCatalogTabs, setShowCatalogTabs] = React.useState(true);
	const headerRef = React.useRef<HTMLDivElement>(null);
	const wrapperRef = React.useRef<HTMLDivElement>(null);
	const stickyHeaderRef = React.useRef<HTMLDivElement>(null);
	const [isContentAlertHidden, setContentAlertHidden] = React.useState(
		getFromLocalStorage(ELocalStorageMobile.SHOWN_CONTENT_INFO) === '1',
	);
	const contentAlertHtml = getConfig().contentInfo.html;
	const shouldShowContentAlert = Boolean(contentAlertHtml) &&
		!IS_SSR &&
		!isContentAlertHidden;
	const theme = getConfig().appearance.theme;

	const onBurgerBtnClick = () => {
		if (topMenuBurgerButton.isBackMode) {
			if (uiStore.topMenuBurgerButton.backUrl) {
				history.push(uiStore.topMenuBurgerButton.backUrl);
			} else if (backPath) {
				const id = backPath?.split('apps/')[1];
				const isPrevSeoAppPage = id ? /^\d+$/g.test(id) : false;

				if (isPrevSeoAppPage) {
					history.push(URLS.APPS);
				} else {
					history.push(backPath);
				}
			} else {
				history.push(URLS.APPS);
			}

			return;
		}

		uiStore.toggleSlideMenu(!slidingMenu.isVisible);
	};

	const onContentAlertCloseClick = () => {
		setContentAlertHidden(true);
		setToLocalStorage(ELocalStorageMobile.SHOWN_CONTENT_INFO, '1');
	};

	useCountersWatchers({
		countersStore,
		currentUserStore,
	});

	React.useEffect(() => {
		return () => {
			setBackPath(pathname);
		};
	}, [pathname]);

	React.useEffect(() => {
		setShowCatalogTabs(isMobileScreen());
	}, []);

	React.useEffect(() => {
		const onResize = throttle(300, () => {
			setShowCatalogTabs(isMobileScreen());
		});

		window.addEventListener('resize', onResize);

		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, []);

	return <div className={s.wrapper} ref={wrapperRef}>
		<div
			className={
				classNames(
					s.stickyHeader,
					{ [`use-theme-${theme}`]: Boolean(theme) },
				)
			}
			ref={stickyHeaderRef}
		>
			<Header
				refProp={headerRef}
				counters={{
					notifies,
					dialogues,
				}}
				isShowIcons={currentUserStore.isMyCreated && uiStore.topMenuIcons.isVisible}
				isBurgerInBackState={topMenuBurgerButton.isBackMode}
				isShownMenu={slidingMenu.isVisible}
				onBurgerBtnClick={onBurgerBtnClick}
				pageTitle={uiStore.pageTitle.isVisible ? uiStore.pageTitle?.value : null}
				balance={currentUserStore.currentUser.sumMailiki || 0}
			/>
			{showCatalogTabs && <CatalogTabs className={s.tabs}/>}
			{shouldShowContentAlert && <Alert
				className={s.alert}
				html={contentAlertHtml}
				onClose={onContentAlertCloseClick}
			/>}
		</div>
	</div>;
}));
